// Product thumbnails on an order row (Phase 3, 2026-08-01).
//
// A Duo order that bought the cover add-on shows two pictures; anything past
// three collapses into a "+N" chip so a row can never grow unbounded as more
// upsells are added later.
//
// DELIBERATELY QUIET. The orders list is scanned, not studied — these are 28px
// squares that overlap slightly, with no border chrome, no hover cards and no
// captions. They are a glance-level cue for "what is in this order", sitting
// beside the formula pill that already carries the text. An order with no
// catalogue picture yet renders nothing at all rather than a grey placeholder
// box, because a column of empty boxes reads as breakage.

import type { OrderItem } from '@/lib/api';

const MAX_SHOWN = 3;

export function OrderItemThumbs({ items }: { items?: OrderItem[] }) {
  if (!items || items.length === 0) return null;

  // Only items we can actually draw. Losing the picture of one line should not
  // hide the others.
  const withImage = items.filter((i) => i.image_url);
  if (withImage.length === 0) return null;

  const shown = withImage.slice(0, MAX_SHOWN);
  const extra = withImage.length - shown.length;

  const label = items
    .map((i) => {
      const name = i.title ?? i.variant_title ?? 'Article';
      return i.quantity > 1 ? `${name} x${i.quantity}` : name;
    })
    .join(' · ');

  return (
    <span className="inline-flex items-center shrink-0" title={label} aria-label={label}>
      {shown.map((item, idx) => (
        <img
          key={`${item.shopify_variant_gid ?? item.kind}-${idx}`}
          src={item.image_url!}
          alt=""
          loading="lazy"
          className="w-7 h-7 rounded-md object-cover bg-gray-100 ring-1 ring-white dark:ring-adm-surface"
          // Slight overlap keeps three pictures inside the width of roughly two,
          // so adding an upsell never pushes the formula pill out of the row.
          style={{ marginLeft: idx === 0 ? 0 : -6, zIndex: shown.length - idx }}
        />
      ))}
      {extra > 0 && (
        <span
          className="ml-1 text-[10px] font-medium text-gray-500 bg-gray-100 rounded-full px-1.5 py-0.5"
          aria-hidden="true"
        >
          +{extra}
        </span>
      )}
    </span>
  );
}
